import { useTranslations } from "next-intl";
import Image from "next/image";

interface ImageData {
  id: string | number;
  src: string;
  alt: string;
}

interface ImageGalleryProps {
  images: ImageData[];
}

export default function ImageGallery({ images }: ImageGalleryProps) {
  const galleryImages = images.slice(0, 7);
  const t = useTranslations("HomePage");
  return (
    <section id="gallery" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Desktop & Tablet Header */}
        <div className="text-center mb-12 md:block hidden">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t("image.tit")}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t("image.sub")}
          </p>
        </div>

        {/* Mobile Header - "Viona Vadrin" Style */}
        <div className="md:hidden mb-8 text-center">
          {/* Mobile title and subtitle */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("image.tit")}
            </h2>
            <p className="text-sm text-gray-600">{t("image.sub")}</p>
          </div>
        </div>

        {/* Desktop Layout (≥1024px) - Irregular CSS Grid Mosaic */}
        <div
          className="hidden lg:grid lg:grid-cols-[1.5fr_1fr_1fr_1.2fr_1fr] lg:grid-rows-3 lg:gap-4 lg:h-[600px]"
          style={{
            gridTemplateAreas: `
                 "a a b c c"
                 "a a b d e"
                 "f g g d e"
               `,
          }}
        >
          {/* Image A - Large left block */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "a" }}
          >
            <Image
              fill
              src={galleryImages[0].src || "/placeholder.svg"}
              alt={galleryImages[0].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image B - Tall middle */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "b" }}
          >
            <Image
              fill
              src={galleryImages[1].src || "/placeholder.svg"}
              alt={galleryImages[1].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image C - Wide top right */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "c" }}
          >
            <Image
              fill
              src={galleryImages[2].src || "/placeholder.svg"}
              alt={galleryImages[2].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image D - Tall right */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "d" }}
          >
            <Image
              fill
              src={galleryImages[3].src || "/placeholder.svg"}
              alt={galleryImages[3].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image E - Square far right */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "e" }}
          >
            <Image
              fill
              src={galleryImages[4].src || "/placeholder.svg"}
              alt={galleryImages[4].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image F - Bottom left */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "f" }}
          >
            <Image
              fill
              src={galleryImages[5].src || "/placeholder.svg"}
              alt={galleryImages[5].alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image G - Bottom center wide */}
          <div
            className="overflow-hidden rounded-none shadow-lg relative"
            style={{ gridArea: "g" }}
          >
            <Image
              fill
              src={galleryImages[6].src || "/placeholder.svg"}
              alt={galleryImages[6].alt}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Tablet Layout (768-1023px) - Two-column collage preserving feel */}
        <div className="hidden md:grid lg:hidden grid-cols-2 gap-3 max-w-4xl mx-auto">
          {/* Left column - Large dominant image */}
          <div className="row-span-2 overflow-hidden rounded-none shadow-lg relative">
            <div className="aspect-[3/4] w-full">
              <Image
                fill
                src={galleryImages[0].src || "/placeholder.svg"}
                alt={galleryImages[0].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right column top - Medium image */}
          <div className="overflow-hidden rounded-none shadow-lg relative">
            <div className="aspect-[4/3] w-full">
              <Image
                fill
                src={galleryImages[1].src || "/placeholder.svg"}
                alt={galleryImages[1].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right column bottom - Medium image */}
          <div className="overflow-hidden rounded-none shadow-lg relative">
            <div className="aspect-[4/3] w-full">
              <Image
                fill
                src={galleryImages[2].src || "/placeholder.svg"}
                alt={galleryImages[2].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Bottom row - Four remaining images spanning full width */}
          <div className="col-span-2 grid grid-cols-4 gap-3 mt-3">
            <div className="overflow-hidden rounded-none shadow-lg">
              <div className="aspect-square w-full relative">
                <Image
                  fill
                  src={galleryImages[3].src || "/placeholder.svg"}
                  alt={galleryImages[3].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-none shadow-lg relative">
              <div className="aspect-square w-full">
                <Image
                  fill
                  src={galleryImages[4].src || "/placeholder.svg"}
                  alt={galleryImages[4].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-none shadow-lg relative">
              <div className="aspect-square w-full">
                <Image
                  fill
                  src={galleryImages[5].src || "/placeholder.svg"}
                  alt={galleryImages[5].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-none shadow-lg relative">
              <div className="aspect-square w-full">
                <Image
                  fill
                  src={galleryImages[6].src || "/placeholder.svg"}
                  alt={galleryImages[6].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout (<768px) - "Viona Vadrin" Style Structured Grid */}
        <div className="md:hidden mt-6 max-w-sm mx-auto">
          {/* Row 1: Two small squares left, one tall portrait right */}
          <div className="grid grid-cols-3 gap-2 mb-2">
            {/* Left column with two stacked squares */}
            <div className="grid grid-rows-2 gap-2">
              <div className="overflow-hidden rounded-none shadow-lg relative">
                <div className="aspect-square w-full">
                  <Image
                    fill
                    src={galleryImages[0].src || "/placeholder.svg"}
                    alt={galleryImages[0].alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="overflow-hidden rounded-none shadow-lg relative">
                <div className="aspect-square w-full">
                  <Image
                    fill
                    src={galleryImages[1].src || "/placeholder.svg"}
                    alt={galleryImages[1].alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Right side - Tall portrait spanning 2 columns */}
            <div className="col-span-2 overflow-hidden rounded-none shadow-lg">
              <div className="aspect-[2/3] w-full relative">
                <Image
                  fill
                  src={galleryImages[2].src || "/placeholder.svg"}
                  alt={galleryImages[2].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Full-width landscape card */}
          <div className="overflow-hidden rounded-none shadow-lg relative mb-2">
            <div className="aspect-[5/2] w-full">
              <Image
                fill
                src={galleryImages[3].src || "/placeholder.svg"}
                alt={galleryImages[3].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Row 3: Two equal square cards side by side */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="overflow-hidden rounded-none shadow-lg relative">
              <div className="aspect-square w-full">
                <Image
                  fill
                  src={galleryImages[4].src || "/placeholder.svg"}
                  alt={galleryImages[4].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-none shadow-lg relative">
              <div className="aspect-square w-full">
                <Image
                  fill
                  src={galleryImages[5].src || "/placeholder.svg"}
                  alt={galleryImages[5].alt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Row 4: Final accent image */}
          <div className="overflow-hidden rounded-none shadow-lg relative">
            <div className="aspect-[5/2] w-full">
              <Image
                fill
                src={galleryImages[6].src || "/placeholder.svg"}
                alt={galleryImages[6].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
